<template>
	<v-row class="full-height customer-category-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="customer-category-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search categories..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="customer-category-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Delete Button (visible when items selected) -->
						<v-col cols="auto" v-if="deleteCategories.length > 0" class="mr-2">
							<v-btn
								depressed
								rounded
								color="error"
								@click="deleteWarning = true"
							>
								<v-icon left>mdi-delete</v-icon>
								Delete ({{ deleteCategories.length }})
							</v-btn>
						</v-col>

						<!-- New Category Button -->
						<v-col cols="auto">
							<v-btn
								depressed
								rounded
								color="success"
								@click="newCategory()"
								:disabled="activeEdit !== null"
							>
								New Customer Category
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 customer-category-table-container">
			<v-data-table
				:headers="headers"
				:items="categories"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="customer-category-data-table"
				fixed-header
				show-select
				v-model="deleteCategories"
				:item-key="'customerCategoryId'"
				:disable-sort="activeEdit !== null"
			>
				<!-- Row attributes for drag and drop -->
				<template v-slot:body="{ items }">
					<tbody
						@drop="onDrop($event)"
						@dragover.prevent="onDragOver($event)"
						@dragenter.prevent
					>
						<tr
							v-for="item in items"
							:key="item.customerCategoryId"
							:data-id="item.customerCategoryId"
							:draggable="activeEdit !== null"
							:class="{ dragging: dragItem === item, 'drag-over': dragOverItem === item }"
							@dragstart="onDragStart($event, item)"
							@dragend="onDragEnd"
						>
							<td>
								<v-simple-checkbox
									v-if="item.customerCategoryId !== null"
									:value="deleteCategories.includes(item)"
									@input="toggleSelection(item, $event)"
									color="accent"
									:disabled="activeEdit !== null"
								></v-simple-checkbox>
							</td>
							<td>
								<template v-if="item === activeEdit">
									<v-text-field
										v-model="item.category"
										outlined
										dense
										hide-details="auto"
										@keyup.enter="saveCategory()"
										@keyup.esc="cancelEdit(item)"
										autofocus
									></v-text-field>
								</template>
								<template v-else>
									{{ item.category }}
								</template>
							</td>
							<td class="text-center sequence-cell">
								<v-icon
									v-if="activeEdit !== null"
									class="drag-handle"
									style="font-size: 2rem; cursor: grab"
								>
									mdi-drag-vertical
								</v-icon>
								<span v-else>{{ item.sequence }}</span>
							</td>
							<td class="text-end">
								<template v-if="item === activeEdit">
									<v-btn
										icon
										color="error"
										@click="cancelEdit(item)"
										title="Cancel"
									>
										<v-icon style="font-size: 2rem">mdi-close</v-icon>
									</v-btn>
									<v-btn
										icon
										color="success"
										@click="saveCategory()"
										title="Save"
									>
										<v-icon style="font-size: 2rem">mdi-check</v-icon>
									</v-btn>
								</template>
								<template v-else>
									<v-btn
										icon
										color="primary"
										@click="activeEdit = item"
										:disabled="activeEdit !== null"
										title="Edit"
									>
										<v-icon style="font-size: 2rem">mdi-pencil</v-icon>
									</v-btn>
								</template>
							</td>
						</tr>
					</tbody>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-tag</v-icon>
						<p class="mt-2 grey--text">No customer categories found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected customer categories?'"
			:itemList="deleteCategories"
			:itemListKey="'category'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteCategory();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { getApiUrl } from "@/helpers";

export default {
	name: "CustomerCategory",
	components: {
		WarningModal,
	},
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
			},
			categories: [],
			totalCount: 0,
			deleteCategories: [],
			deleteWarning: false,
			activeEdit: null,
			searchTimeout: null,
			dragItem: null,
			dragOverItem: null,
			originalSequences: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["sequence"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Category",
					value: "category",
					sortable: true,
					width: "70%",
				},
				{
					text: "Sequence",
					value: "sequence",
					sortable: true,
					width: "15%",
					align: "center",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "15%",
					align: "end",
				},
			],
		};
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
	},
	methods: {
		toggleSelection(item, isSelected) {
			if (isSelected) {
				if (!this.deleteCategories.includes(item)) {
					this.deleteCategories.push(item);
				}
			} else {
				this.deleteCategories = this.deleteCategories.filter(c => c !== item);
			}
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				category: "category",
				sequence: "sequence",
			};
			return sortFieldMap[columnValue] || "sequence";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "sequence";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			// Build where clause - only include search filter if there's a search term
			let where = null;
			if (keyword) {
				where = {
					category: { contains: keyword },
				};
			}

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				where,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetCustomerCategories(
							$offset: Int!
							$take: Int!
							$order: [CustomerCategorySortInput!] = [{ sequence: ASC }]
							$where: CustomerCategoryFilterInput
						) {
							customerCategories(
								skip: $offset
								take: $take
								order: $order
								where: $where
							) {
								totalCount
								items {
									customerCategoryId
									category
									sequence
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.customerCategories.totalCount;
				this.categories = res.data.customerCategories.items;
				this.loading = false;
			} catch (error) {
				console.error("Error fetching customer categories:", error);
				this.loading = false;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchForm.keywords = "";
			this.tableOptions.page = 1;
			this.fetchData();
		},

		newCategory() {
			const newCategory = {
				customerCategoryId: null,
				category: "",
				sequence: this.categories.length > 0 ? Math.max(...this.categories.map(c => c.sequence)) + 1 : 1,
			};

			this.categories.unshift(newCategory);
			this.activeEdit = newCategory;
		},

		cancelEdit(item) {
			if (item.customerCategoryId === null) {
				// Remove new unsaved category
				this.categories = this.categories.filter(c => c.customerCategoryId !== null);
			}
			this.activeEdit = null;
			this.originalSequences = null;
			this.fetchData();
		},

		async deleteCategory() {
			try {
				for await (const cat of this.deleteCategories) {
					await this.$http.delete(
						getApiUrl(`/api/settings/customer-category/${cat.customerCategoryId}`)
					);
				}

				this.deleteCategories = [];
				this.fetchData();
			} catch (error) {
				console.error("Error deleting customer categories:", error);
			}
		},

		async saveCategory() {
			try {
				if (!this.activeEdit.category || this.activeEdit.category.trim() === "") {
					alert("Category name is required");
					return;
				}

				// Save the active edit item first
				console.log('[CustomerCategory] Saving activeEdit:', this.activeEdit);
				if (this.activeEdit.customerCategoryId) {
					await this.$http.put(
						getApiUrl("/api/settings/customer-category"),
						this.activeEdit
					);
				} else {
					await this.$http.post(
						getApiUrl("/api/settings/customer-category"),
						this.activeEdit
					);
				}

				// Save all other items that have changed sequences (from drag-and-drop)
				console.log('[CustomerCategory] originalSequences:', this.originalSequences);
				console.log('[CustomerCategory] current categories:', this.categories.map(c => ({ id: c.customerCategoryId, seq: c.sequence, cat: c.category })));

				if (this.originalSequences) {
					const itemsToSave = [];
					for (const item of this.categories) {
						if (item.customerCategoryId &&
							item !== this.activeEdit &&
							this.originalSequences[item.customerCategoryId] !== item.sequence) {
							itemsToSave.push({ id: item.customerCategoryId, oldSeq: this.originalSequences[item.customerCategoryId], newSeq: item.sequence });
							console.log('[CustomerCategory] Saving sequence change for:', item.customerCategoryId, 'from', this.originalSequences[item.customerCategoryId], 'to', item.sequence);
							await this.$http.put(
								getApiUrl("/api/settings/customer-category"),
								item
							);
						}
					}
					console.log('[CustomerCategory] Items saved with sequence changes:', itemsToSave);
					this.originalSequences = null;
				} else {
					console.log('[CustomerCategory] No originalSequences - no drag-and-drop occurred');
				}

				this.activeEdit = null;
				this.fetchData();
			} catch (error) {
				console.error("Error saving customer category:", error);
			}
		},

		// Drag and drop sorting functions
		onDragStart(event, item) {
			console.log('[CustomerCategory] onDragStart - item:', item.customerCategoryId, item.category);

			// Capture original sequences before drag-and-drop starts
			if (!this.originalSequences) {
				this.originalSequences = {};
				this.categories.forEach(cat => {
					if (cat.customerCategoryId) {
						this.originalSequences[cat.customerCategoryId] = cat.sequence;
					}
				});
				console.log('[CustomerCategory] Captured originalSequences:', { ...this.originalSequences });
			}

			this.dragItem = item;
			event.dataTransfer.effectAllowed = 'move';
			event.dataTransfer.setData('text/plain', item.customerCategoryId);

			// Add visual feedback
			setTimeout(() => {
				event.target.classList.add('dragging');
			}, 0);
		},

		onDragEnd(event) {
			console.log('[CustomerCategory] onDragEnd');
			event.target.classList.remove('dragging');
			this.dragItem = null;
			this.dragOverItem = null;
		},

		onDragOver(event) {
			event.preventDefault();
			const targetRow = event.target.closest('tr');
			if (!targetRow || !this.dragItem) return;

			const targetId = parseInt(targetRow.dataset.id);
			const targetItem = this.categories.find(c => c.customerCategoryId === targetId);

			if (targetItem && targetItem !== this.dragItem) {
				this.dragOverItem = targetItem;

				// Get the position to insert
				const dragIndex = this.categories.findIndex(c => c === this.dragItem);
				const targetIndex = this.categories.findIndex(c => c === targetItem);

				if (dragIndex !== -1 && targetIndex !== -1 && dragIndex !== targetIndex) {
					console.log('[CustomerCategory] Reordering: moving', this.dragItem.customerCategoryId, 'from index', dragIndex, 'to', targetIndex);

					// Remove the dragged item from its current position
					const [removed] = this.categories.splice(dragIndex, 1);
					// Insert at the new position
					this.categories.splice(targetIndex, 0, removed);

					// Update sequences
					this.categories.forEach((cat, index) => {
						cat.sequence = index + 1;
					});

					console.log('[CustomerCategory] New order:', this.categories.map(c => ({ id: c.customerCategoryId, seq: c.sequence })));
				}
			}
		},

		onDrop(event) {
			event.preventDefault();
			console.log('[CustomerCategory] onDrop - final sequences:', this.categories.map(c => ({ id: c.customerCategoryId, seq: c.sequence })));
			this.dragOverItem = null;
			// The reordering is already done in onDragOver, just clean up
		},
	},
	mounted() {
		// Initial fetch will happen via tableOptions watcher
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
};
</script>

<style lang="scss" scoped>
.customer-category-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.customer-category-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.customer-category-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.customer-category-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.customer-category-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}

		tbody tr {
			transition: background-color 0.2s;

			&.dragging {
				opacity: 0.4;
				background-color: $color-gray-light !important;
			}

			&.drag-over {
				border-top: 2px solid $color-tertiary;
			}

			&[draggable="true"] {
				cursor: grab;

				&:active {
					cursor: grabbing;
				}
			}
		}

		.drag-handle {
			color: $color-gray-dark;

			&:hover {
				color: $color-tertiary;
			}
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
